import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface DataColumn<T> {
  key: string;
  header: ReactNode;
  ariaLabel?: string;
  render: (row: T) => ReactNode;
  /** Presence makes the column sortable (header click cycles asc → desc → off). */
  sortValue?: (row: T) => string | number;
}

export interface TableSort {
  key: string;
  dir: 'asc' | 'desc';
}

/** Server-driven mode: the screen owns fetching; the table renders `rows`
 *  as-is (no client filtering/sorting/slicing) and only emits callbacks. */
export interface ServerTableState {
  /** Filtered total reported by the server (`Paginated.total`). */
  total: number;
  /** 0-based current page. */
  page: number;
  onPageChange: (page: number) => void;
  /** When present, the search box is shown and forwards input verbatim —
   *  debouncing is the screen's responsibility. */
  onSearchChange?: (query: string) => void;
}

export interface TableStateInput<T> {
  query?: string;
  searchText?: (row: T) => string;
  sort?: TableSort;
  page?: number;
  pageSize?: number;
}

/** Pure client-mode pipeline: filter → sort → clamp page → slice. Kept out of
 *  the component so the node-side test suite can cover it without a DOM. */
export function applyTableState<T>(
  rows: T[],
  columns: DataColumn<T>[],
  state: TableStateInput<T>
): { pageRows: T[]; total: number; pageCount: number; page: number } {
  let result = rows;
  const needle = (state.query ?? '').trim().toLowerCase();
  if (needle && state.searchText) {
    const searchText = state.searchText;
    result = result.filter((row) => searchText(row).toLowerCase().includes(needle));
  }
  const sortValue = state.sort ? columns.find((c) => c.key === state.sort?.key)?.sortValue : undefined;
  if (state.sort && sortValue) {
    const factor = state.sort.dir === 'desc' ? -1 : 1;
    result = [...result].sort((a, b) => {
      const va = sortValue(a);
      const vb = sortValue(b);
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return 0;
    });
  }
  const total = result.length;
  if (!state.pageSize) return { pageRows: result, total, pageCount: 1, page: 0 };
  const pageCount = Math.max(1, Math.ceil(total / state.pageSize));
  const page = Math.min(Math.max(state.page ?? 0, 0), pageCount - 1);
  return {
    pageRows: result.slice(page * state.pageSize, (page + 1) * state.pageSize),
    total,
    pageCount,
    page
  };
}

export function DataTable<T>({
  rows,
  columns,
  empty,
  searchText,
  searchPlaceholder,
  defaultSort,
  pageSize,
  getRowKey,
  server,
  mobileCards = false
}: {
  rows: T[];
  columns: DataColumn<T>[];
  empty: string;
  /** Enables the client-side search box. */
  searchText?: (row: T) => string;
  searchPlaceholder?: string;
  defaultSort?: TableSort;
  /** Enables the pager. In server mode this is the server page size. */
  pageSize?: number;
  /** Stable row keys — required for correct re-renders once sorting/paging
   *  reorder rows; falls back to the array index. */
  getRowKey?: (row: T) => string;
  server?: ServerTableState;
  /** Opt-in compact card layout below the phone breakpoint. */
  mobileCards?: boolean;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<TableSort | undefined>(defaultSort);
  const [page, setPage] = useState(0);

  const derived = useMemo(() => {
    if (server) {
      const pageCount = pageSize ? Math.max(1, Math.ceil(server.total / pageSize)) : 1;
      return { pageRows: rows, total: server.total, pageCount, page: server.page };
    }
    return applyTableState(rows, columns, { query, searchText, sort, page, pageSize });
  }, [rows, columns, query, searchText, sort, page, pageSize, server]);

  const showSearch = Boolean(server ? server.onSearchChange : searchText);
  const showPager = Boolean(server || pageSize);
  const sortable = (column: DataColumn<T>) => !server && Boolean(column.sortValue);

  const onSearch = (value: string) => {
    setQuery(value);
    setPage(0); // a narrower result set must never strand the user on an empty page
    server?.onSearchChange?.(value);
  };
  const onSort = (column: DataColumn<T>) => {
    if (!sortable(column)) return;
    setPage(0);
    setSort((current) => {
      if (current?.key !== column.key) return { key: column.key, dir: 'asc' };
      if (current.dir === 'asc') return { key: column.key, dir: 'desc' };
      return undefined;
    });
  };
  const onPage = (next: number) => {
    if (server) server.onPageChange(next);
    else setPage(next);
  };

  const table = (
    <div className={mobileCards ? 'table-wrap mobile-cards' : 'table-wrap'}>
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={sortable(column) ? 'sortable' : undefined}
                onClick={() => onSort(column)}
              >
                {column.header}
                {sort?.key === column.key ? (
                  <span className="sort-indicator">{sort.dir === 'asc' ? ' ▲' : ' ▼'}</span>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {derived.pageRows.map((row, index) => (
            <tr key={getRowKey ? getRowKey(row) : index}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  data-label={
                    column.ariaLabel ??
                    (typeof column.header === 'string' ? column.header : undefined)
                  }
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
          {derived.pageRows.length === 0 ? (
            <tr>
              <td className="empty" colSpan={columns.length}>
                {empty}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );

  if (!showSearch && !showPager) return table;

  return (
    <div className="table-shell">
      {showSearch ? (
        <div className="table-toolbar">
          <input
            type="search"
            placeholder={searchPlaceholder ?? t('table.searchPlaceholder')}
            value={query}
            onChange={(event) => onSearch(event.target.value)}
          />
          <span className="table-count">
            {t('table.rowCount', { count: derived.total })}
          </span>
        </div>
      ) : null}
      {table}
      {showPager ? (
        <div className="table-pager">
          <button type="button" className="ghost" disabled={derived.page <= 0} onClick={() => onPage(derived.page - 1)}>
            {t('table.prev')}
          </button>
          <span>
            {t('table.pageOf', { page: derived.page + 1, pageCount: derived.pageCount })}
          </span>
          <button
            type="button"
            className="ghost"
            disabled={derived.page >= derived.pageCount - 1}
            onClick={() => onPage(derived.page + 1)}
          >
            {t('table.next')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
